import { type StationClosureFormFields } from '@centric-os/types';
import { computed } from 'vue';
import {
  isCalendarRevisionConflictError,
  useCalendarStore,
} from '../../../routes/stores/use-calendar-store';
import { DateTime } from 'luxon';
import { useDateHelper } from '@centric-os/helpers';
import { storeToRefs } from 'pinia';
import useCalendar from '../use-calendar';
import { useStationStore } from '../../../routes/stores/use-station-store';
import useGlobalToast from '@centric-os/components/src/core/messages/toast/composables/use-global-toast';
import { useStationScheduleStore } from '../../../routes/stores';
import useCalendarRevisionConflictFeedback from '../use-calendar-revision-conflict-feedback';
import { useI18n } from 'vue-i18n';

/**
 * Composable encapsulating functions used to manage the Station Closure form
 */
export default () => {
  // stores
  const calendarStore = useCalendarStore();
  const { currentEvent } = storeToRefs(calendarStore);
  const stationStore = useStationStore();
  const { stationBrand } = storeToRefs(stationStore);
  const stationScheduleStore = useStationScheduleStore();
  const { showStationClosureForm } = storeToRefs(stationScheduleStore);

  // composables
  const { getDateFromTimestamp } = useDateHelper();
  const { prepClosureEventForSave } = useCalendar();
  const { t } = useI18n();
  const { showSuccessToast, showErrorToast } = useGlobalToast();
  const { showCalendarRevisionConflictToast } = useCalendarRevisionConflictFeedback();

  /**
   * Computed for the initial values of the Station Closure form, using current data if it editing
   */
  const initialValues = computed<StationClosureFormFields>(() => {
    const timezone = calendarStore.stationTimezone();
    // Today at the station, as the pickers' local wall clock
    const today = DateTime.now()
      .setZone(timezone)
      .startOf('day')
      .setZone('local', { keepLocalTime: true })
      .toJSDate();
    let startDateTime = today;
    let endDateTime = today;
    let reason = '';

    if (currentEvent?.value) {
      const { data, schedule } = currentEvent.value;
      reason = data?.title;

      startDateTime = schedule.start ? getDateFromTimestamp(schedule.start, timezone) : undefined;
      endDateTime = schedule.end ? getDateFromTimestamp(schedule.end, timezone) : undefined;
    }

    return {
      startDateTime,
      endDateTime,
      reason,
    };
  });

  /**
   * Saves the current form data for a Station Closure form
   * @param formData The form data to save
   * @param isDelete Is this save a delete action?
   * @returns when the save completes or fails
   */
  const saveClosure = async (
    formData: StationClosureFormFields,
    isDelete: boolean = false,
  ): Promise<void> => {
    try {
      calendarStore.currentEvent = prepClosureEventForSave(
        formData,
        calendarStore.currentEvent,
        calendarStore.stationTimezone(),
      );
      await calendarStore.saveCalendarEvents(stationBrand.value?.id, true, isDelete);
      await calendarStore.getAllLocationCalendarEvents([stationBrand.value?.id], true);
      showSuccessToast(t('sites.station.closure.sidebar.formSaveSuccess'));
      showStationClosureForm.value = false;
    } catch (err) {
      if (isCalendarRevisionConflictError(err)) {
        showCalendarRevisionConflictToast();
        return;
      }
      console.error('Failed updating station closures', calendarStore.currentEvent, formData, err);
      showErrorToast(t('sites.station.closure.sidebar.formSaveFailure'));
    } finally {
      currentEvent.value = undefined;
    }
  };

  return { initialValues, saveClosure };
};
